/**
 * CSV of what is currently filtered.  journeys, cross-cutting: "every list
 * screen needs … CSV export of what is currently filtered"
 *
 * Composed in the browser rather than served as an endpoint, for the same §3.1
 * reason the Price and On sale columns are: `catalog` may not read `commerce`.
 * The screen already assembles those two from the modules that own them, and an
 * export that dropped them would not be an export of what is on the screen —
 * it would be a different report that happens to share a filter.
 *
 * It is therefore bounded, and says so out loud rather than quietly truncating.
 */

/** Excel-in-Finnish, which is who opens these. */
const DELIMITER = ';';

/**
 * How many rows an export will fetch before it refuses.
 *
 * Not a technical limit — it is the point where "export the filtered list" has
 * stopped being that and become "export the catalogue", which wants a job on
 * the server, not several hundred round trips from a browser tab. The operator
 * is told the number and told to narrow the filter, which is a better answer
 * than a file that is quietly missing its tail.
 */
export const EXPORT_LIMIT = 2000;

export class ExportTooLarge extends Error {
  constructor(readonly total: number) {
    super(
      `${total} products match, and an export is limited to ${EXPORT_LIMIT}. ` +
        'Narrow the filter and try again.',
    );
    this.name = 'ExportTooLarge';
  }
}

/**
 * One field, quoted only when it has to be.
 *
 * A leading `=`, `+`, `-` or `@` is prefixed with an apostrophe: a spreadsheet
 * treats those as the start of a formula, and a product code beginning with a
 * minus should not execute in somebody's Excel.
 */
function field(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  const defused = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /["\n\r;,]/.test(defused) ? `"${defused.replaceAll('"', '""')}"` : defused;
}

export function toCsv(headers: string[], rows: (string | number | null | undefined)[][]): string {
  const lines = [headers, ...rows].map((row) => row.map(field).join(DELIMITER));
  // CRLF and a BOM: between them they are the difference between a file Excel
  // opens as Finnish text and one it opens as mojibake. The BOM is written as
  // an escape because an invisible character in a template literal is the kind
  // of thing that gets deleted by accident.
  return '\ufeff' + lines.join('\r\n') + '\r\n';
}

/** Hands the file to the browser. Revoked immediately; the download is already queued. */
export function downloadCsv(filename: string, csv: string): void {
  downloadBlob(filename, new Blob([csv], { type: 'text/csv;charset=utf-8' }));
}

/**
 * Hands any blob to the browser as a named download.
 *
 * SEPARATE FROM `downloadCsv` BECAUSE NOT EVERY FILE IS TEXT WE BUILT. A report
 * the server generates arrives as bytes over an authenticated request, and a
 * plain `<a href>` cannot fetch it: this app authenticates with a bearer token
 * held in memory, and a link navigation carries no headers. That is not a
 * theory — the settlement workbook shipped as a link and answered 401.
 */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** `products-2026-08-19.csv` — sortable, and obvious a week later. */
export function stampedName(prefix: string, at = new Date()): string {
  const iso = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
  return `${prefix}-${iso}.csv`;
}
